<template>
  <aside
    class="app-sidebar"
    :class="{ 'app-sidebar--collapsed': collapsed }"
    :style="{ width: collapsed ? '64px' : '240px' }"
  >
    <div class="sidebar-container">
      <!-- Logo -->
      <div class="sidebar-logo">
        <img src="/logo-sidebar.png" alt="飞利索智投" width="24" height="24" />
        <span v-if="!collapsed" class="logo-text">飞利索智投</span>
      </div>

      <!-- 菜单滚动区域 -->
      <div class="sidebar-menu-wrapper">
        <n-menu
          :value="activeKey"
          :collapsed="collapsed"
          :collapsed-width="64"
          :options="menuOptions"
          @update:value="handleMenuSelect"
        />
      </div>

      <!-- 用户信息 -->
      <div class="sidebar-user" :class="{ 'sidebar-user-collapsed': collapsed }">
        <n-avatar :size="collapsed ? 32 : 36" color="#409EFF">{{ userInitial }}</n-avatar>
        <div v-if="!collapsed" class="user-info">
          <div class="user-name">{{ displayName }}</div>
          <n-tag :type="authStore.hasQianchuanAuth ? 'success' : 'warning'" size="small">
            {{ authStore.hasQianchuanAuth ? '已授权' : '未授权' }}
          </n-tag>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { h, computed, inject, ref } from 'vue'
import { NMenu, NIcon, NAvatar, NTag } from 'naive-ui'
import { RocketOutline as Rocket, StorefrontOutline as Shop, KeyOutline as Key, ShieldOutline as Shield, ListOutline as List, PeopleOutline as People, BagOutline as Bag, FlashOutline as Flash, ImagesOutline as Images, WaterOutline as Water, GitBranchOutline as GitBranch, BarChartOutline as BarChart, CardOutline as Card, NotificationsOutline as Notifications, TrophyOutline as Trophy, SettingsOutline as Settings, MailOutline as Mail, WalletOutline as Wallet, TrashOutline as Trash, PeopleCircleOutline as PeopleCircle, TimerOutline as Timer } from '@vicons/ionicons5'
import type { MenuOption } from 'naive-ui'
import { getPromptFactoryPublicUrl } from '~/utils/promptFactoryPublicUrl'
import { getOpsCenterPublicUrl } from '~/utils/opsCenterPublicUrl'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()

const collapsed = inject('sidebarCollapsed', ref(false))

const displayName = computed(() => {
  return authStore.user?.real_name || authStore.user?.username || '用户'
})

const userInitial = computed(() => {
  const name = displayName.value
  return name.charAt(0).toUpperCase() || 'U'
})

const activeKey = computed(() => {
  const path = route.path
  if (path.startsWith('/advertisers')) return 'advertisers'
  if (path.startsWith('/campaigns')) return 'campaigns'
  if (path.startsWith('/products')) return 'products'
  if (path.startsWith('/auto-placement')) return 'auto-placement'
  if (path.startsWith('/budget-settings')) return 'budget-settings'
  if (path.startsWith('/auto-material')) return 'auto-material'
  if (path.startsWith('/material-inspect')) return 'material-inspect'
  if (path.startsWith('/algorithm-tasks')) return 'algorithm-tasks'
  if (path.startsWith('/invalid-campaign-management')) return 'invalid-campaign-management'
  if (path.startsWith('/campaign-status-schedule')) return 'campaign-status-schedule'
  if (path.startsWith('/seedling')) return 'seedling'
  if (path.startsWith('/class-u')) return 'class-u'
  if (path.startsWith('/auth/qianchuan')) return 'qianchuan'
  if (path.startsWith('/org-grants')) return 'org-grants'
  if (path.startsWith('/rpa/tokens')) return 'rpa-tokens'
  if (path.startsWith('/package-center')) return 'package-center'
  if (path.startsWith('/messages')) return 'messages'
  if (path.startsWith('/admin/users')) return 'admin-users'
  if (path.startsWith('/admin/orgs')) return 'admin-orgs'
  if (path.startsWith('/admin/system-configs')) return 'system-configs'
  if (path.startsWith('/admin/qianchuan-oauth-apps')) return 'admin-qianchuan-oauth-apps'
  if (path.startsWith('/admin/shop-diagnosis')) return 'admin-shop-diagnosis'
  if (path.startsWith('/admin/historical-to-repair')) return 'admin-historical-to-repair'
  if (path.startsWith('/admin/data-cleanup')) return 'admin-data-cleanup'
  if (path.startsWith('/admin/sync-periodic-runs')) return 'admin-sync-periodic-runs'
  if (path.startsWith('/admin/burst-state-coef')) return 'admin-burst-state-coef'
  if (path.startsWith('/admin/ops-f')) return 'admin-ops-f'
  if (path.startsWith('/admin/ops-adjustment-amplitude')) return 'admin-ops-adjustment-amplitude'
  if (path.startsWith('/admin/ops-auto-opt')) return 'admin-ops-auto-opt'
  if (path.startsWith('/admin/ops-rp-rankings')) return 'admin-ops-rp-rankings'
  if (path.startsWith('/admin/roi-rule-restore')) return 'admin-roi-rule-restore'
  if (path.startsWith('/report-data')) return 'report-data'
  if (path.startsWith('/org-operation-logs')) return 'org-operation-logs'
  if (path.startsWith('/materials')) return 'materials'
  if (path.startsWith('/ai-video-prompts')) return 'ai-video-prompts'
  if (path.startsWith('/ai-paid-video')) return 'ai-paid-video'
  if (path.startsWith('/prompt-factory')) return 'prompt-factory'
  if (path.startsWith('/account-email')) return 'account-email'
  return null
})

const menuOptions = computed<MenuOption[]>(() => {
  const baseOptions: MenuOption[] = [
    {
      label: '账号邮箱',
      key: 'account-email',
      icon: () => h(NIcon, null, { default: () => h(Mail) })
    },
    {
      label: '千川授权',
      key: 'qianchuan',
      icon: () => h(NIcon, null, { default: () => h(Key) })
    },
    {
      label: '服务授权',
      key: 'org-grants',
      icon: () => h(NIcon, null, { default: () => h(PeopleCircle) })
    },
    {
      label: '店铺管理',
      key: 'advertisers',
      icon: () => h(NIcon, null, { default: () => h(Shop) })
    },
    {
      label: '智能 ROI',
      key: 'algorithm-tasks',
      icon: () => h(NIcon, null, { default: () => h(List) })
    },
    {
      label: '商品列表',
      key: 'products',
      icon: () => h(NIcon, null, { default: () => h(Bag) })
    },
    {
      label: '智能投放',
      key: 'auto-placement',
      icon: () => h(NIcon, null, { default: () => h(Flash) })
    },
    {
      label: '预算管理',
      key: 'budget-settings',
      icon: () => h(NIcon, null, { default: () => h(Wallet) })
    },
    {
      label: '智能素材',
      key: 'material-group',
      icon: () => h(NIcon, null, { default: () => h(Images) }),
      children: [
        {
          label: '自动素材',
          key: 'auto-material'
        },
        {
          label: '素材排查',
          key: 'material-inspect'
        },
        {
          label: '素材生产',
          key: 'materials'
        },
        ...(authStore.hasAdminAccess
          ? [
              { label: 'AI视频提示词', key: 'ai-video-prompts' },
              { label: 'AI付费视频', key: 'ai-paid-video' }
            ]
          : [])
      ]
    },
    {
      label: '视频工厂',
      key: 'prompt-factory',
      icon: () => h(NIcon, null, { default: () => h(GitBranch) })
    },
    {
      label: '运维大盘',
      key: 'ops-center',
      icon: () => h(NIcon, null, { default: () => h(BarChart) })
    },
    {
      label: '无效计划',
      key: 'invalid-campaign-management',
      icon: () => h(NIcon, null, { default: () => h(Trash) })
    },
    {
      label: '定时启停',
      key: 'campaign-status-schedule',
      icon: () => h(NIcon, null, { default: () => h(Timer) })
    },
    {
      label: 'RPA 配置',
      key: 'rpa-tokens',
      icon: () => h(NIcon, null, { default: () => h(Shield) })
    },
    // 套餐中心仅付费渠道用户可见
    ...(authStore.isPayUser ? [{
      label: '套餐中心',
      key: 'package-center',
      icon: () => h(NIcon, null, { default: () => h(Card) })
    }] : []),
    {
      label: '消息中心',
      key: 'messages',
      icon: () => h(NIcon, null, { default: () => h(Notifications) })
    },
    {
      label: '数据报表',
      key: 'campaigns',
      icon: () => h(NIcon, null, { default: () => h(Trophy) })
    },
    {
      label: '操作日志',
      key: 'org-operation-logs',
      icon: () => h(NIcon, null, { default: () => h(List) })
    },

  ]

  // 组织代登：按授权权限裁剪菜单，并隐藏账号体系/管理项
  if (authStore.orgImpersonating) {
    const allowKey = (key: string) => {
      if (key === 'advertisers') {
        return (
          authStore.hasOrgPerm('shop.view') ||
          authStore.hasOrgPerm('campaign.view') ||
          authStore.hasOrgPerm('shop.config.view')
        )
      }
      if (key === 'products') return authStore.hasOrgPerm('product.view')
      if (key === 'auto-placement') return authStore.hasOrgPerm('auto_placement.view')
      if (key === 'algorithm-tasks') return authStore.hasOrgPerm('algorithm.view')
      if (key === 'budget-settings') {
        return (
          authStore.hasOrgPerm('budget.manage') ||
          authStore.hasOrgPerm('roi.manage') ||
          authStore.hasOrgPerm('algorithm.view')
        )
      }
      if (key === 'rpa-tokens') return authStore.hasOrgPerm('rpa.view')
      if (key === 'invalid-campaign-management') return authStore.hasOrgPerm('invalid_campaign.view')
      if (key === 'campaign-status-schedule') return authStore.hasOrgPerm('campaign.view')
      if (key === 'package-center') return authStore.hasOrgPerm('package.view')
      if (key === 'messages') return authStore.hasOrgPerm('message.view')
      // 代登不开放数据报表 / 操作日志 / 整组智能素材
      return false
    }
    const filtered = baseOptions.filter((o) => allowKey(String(o.key)))
    // 有 material.view 时插入一级「素材排查」（放在智能投放后）
    if (authStore.hasOrgPerm('material.view')) {
      const inspectItem: MenuOption = {
        label: '素材排查',
        key: 'material-inspect',
        icon: () => h(NIcon, null, { default: () => h(Images) }),
      }
      const autoIdx = filtered.findIndex((o) => String(o.key) === 'auto-placement')
      if (autoIdx >= 0) filtered.splice(autoIdx + 1, 0, inspectItem)
      else filtered.push(inspectItem)
    }
    return filtered
  }

  // 超级管理员专属菜单（代登状态下也可见）
  if (authStore.hasAdminAccess) {
    baseOptions.push({
      label: '系统管理',
      key: 'admin-group',
      icon: () => h(NIcon, null, { default: () => h(Settings) }),
      children: [
        {
          label: 'Seedling培育',
          key: 'seedling'
        },
        {
          label: 'U类列表',
          key: 'class-u'
        },
        {
          label: '工具',
          key: 'report-data'
        },
        {
          label: '账户管理',
          key: 'admin-users'
        },
        {
          label: '组织管理',
          key: 'admin-orgs'
        },
        {
          label: '系统配置',
          key: 'system-configs'
        },
        {
          label: '千川OAuth应用',
          key: 'admin-qianchuan-oauth-apps'
        },
        {
          label: '诊断店铺',
          key: 'admin-shop-diagnosis'
        },
        {
          label: '历史TO补拉',
          key: 'admin-historical-to-repair'
        },
        {
          label: '同步运行',
          key: 'admin-sync-periodic-runs'
        },
        ...(authStore.isSuperAdmin
          ? [{
              label: '数据清理',
              key: 'admin-data-cleanup'
            }]
          : []),
        {
          label: '爆链状态系数',
          key: 'admin-burst-state-coef'
        },
        {
          label: '运维 F 值',
          key: 'admin-ops-f'
        },
        {
          label: '运维调整幅度',
          key: 'admin-ops-adjustment-amplitude'
        },
        {
          label: '自动优配',
          key: 'admin-ops-auto-opt'
        },
        {
          label: 'RP 名单',
          key: 'admin-ops-rp-rankings'
        },
        {
          label: 'ROI规则回滚',
          key: 'admin-roi-rule-restore'
        }
      ]
    })
  }

  return baseOptions
})

const addTab = inject<(tab: any) => void>('addTab', () => {})

const handleMenuSelect = (key: string) => {
  if (key === 'advertisers') {
    router.push('/advertisers')
  } else if (key === 'campaigns') {
    addTab({
      key: 'campaigns',
      title: '数据报表',
      path: '/campaigns',
      closable: true
    })
  } else if (key === 'products') {
    addTab({
      key: 'products',
      title: '商品列表',
      path: '/products',
      closable: true
    })
  } else if (key === 'auto-placement') {
    addTab({
      key: 'auto-placement',
      title: '智能投放',
      path: '/auto-placement',
      closable: true
    })
  } else if (key === 'budget-settings') {
    addTab({
      key: 'budget-settings',
      title: '预算管理',
      path: '/budget-settings',
      closable: true
    })
  } else if (key === 'auto-material') {
    addTab({
      key: 'auto-material',
      title: '自动素材',
      path: '/auto-material',
      closable: true
    })
  } else if (key === 'material-inspect') {
    addTab({
      key: 'material-inspect',
      title: '素材排查',
      path: '/material-inspect',
      closable: true
    })
  } else if (key === 'algorithm-tasks') {
    router.push('/algorithm-tasks')
  } else if (key === 'invalid-campaign-management') {
    addTab({
      key: 'invalid-campaign-management',
      title: '无效计划',
      path: '/invalid-campaign-management',
      closable: true
    })
  } else if (key === 'campaign-status-schedule') {
    addTab({
      key: 'campaign-status-schedule',
      title: '定时启停',
      path: '/campaign-status-schedule',
      closable: true
    })
  } else if (key === 'seedling') {
    addTab({
      key: 'seedling',
      title: 'Seedling培育',
      path: '/seedling',
      closable: true
    })
  } else if (key === 'class-u') {
    addTab({
      key: 'class-u',
      title: 'U类列表',
      path: '/class-u',
      closable: true
    })
  } else if (key === 'prompt-factory') {
    if (typeof window !== 'undefined') {
      window.open(getPromptFactoryPublicUrl(), '_blank', 'noopener,noreferrer')
    }
  } else if (key === 'ops-center') {
    if (typeof window !== 'undefined') {
      window.open(getOpsCenterPublicUrl(), '_blank', 'noopener,noreferrer')
    }
  } else if (key === 'qianchuan') {
    router.push('/auth/qianchuan')
  } else if (key === 'org-grants') {
    addTab({
      key: 'org-grants',
      title: '服务授权',
      path: '/org-grants',
      closable: true
    })
  } else if (key === 'org-operation-logs') {
    addTab({
      key: 'org-operation-logs',
      title: '操作日志',
      path: '/org-operation-logs',
      closable: true
    })
  } else if (key === 'rpa-tokens') {
    router.push('/rpa/tokens')
  } else if (key === 'account-email') {
    addTab({
      key: 'account-email',
      title: '账号邮箱',
      path: '/account-email',
      closable: true
    })
  } else if (key === 'package-center') {
    addTab({
      key: 'package-center',
      title: '套餐中心',
      path: '/package-center',
      closable: true
    })
  } else if (key === 'messages') {
    addTab({
      key: 'messages',
      title: '消息中心',
      path: '/messages',
      closable: true
    })
  } else if (key === 'materials') {
    addTab({
      key: 'materials',
      title: '素材生产',
      path: '/materials',
      closable: true
    })
  } else if (key === 'ai-video-prompts') {
    addTab({
      key: 'ai-video-prompts',
      title: 'AI视频提示词',
      path: '/ai-video-prompts',
      closable: true
    })
  } else if (key === 'ai-paid-video') {
    addTab({
      key: 'ai-paid-video',
      title: 'AI付费视频',
      path: '/ai-paid-video',
      closable: true
    })
  } else if (key === 'admin-users') {
    addTab({
      key: 'admin-users',
      title: '账户管理',
      path: '/admin/users',
      closable: true
    })
  } else if (key === 'admin-orgs') {
    addTab({
      key: 'admin-orgs',
      title: '组织管理',
      path: '/admin/orgs',
      closable: true
    })
  } else if (key === 'system-configs') {
    addTab({
      key: 'system-configs',
      title: '系统配置',
      path: '/admin/system-configs',
      closable: true
    })
  } else if (key === 'admin-qianchuan-oauth-apps') {
    addTab({
      key: 'admin-qianchuan-oauth-apps',
      title: '千川OAuth应用',
      path: '/admin/qianchuan-oauth-apps',
      closable: true
    })
  } else if (key === 'admin-shop-diagnosis') {
    addTab({
      key: 'admin-shop-diagnosis',
      title: '诊断店铺',
      path: '/admin/shop-diagnosis',
      closable: true
    })
  } else if (key === 'admin-historical-to-repair') {
    addTab({
      key: 'admin-historical-to-repair',
      title: '历史TO补拉',
      path: '/admin/historical-to-repair',
      closable: true
    })
  } else if (key === 'admin-sync-periodic-runs') {
    addTab({
      key: 'admin-sync-periodic-runs',
      title: '同步运行',
      path: '/admin/sync-periodic-runs',
      closable: true
    })
  } else if (key === 'admin-data-cleanup') {
    addTab({
      key: 'admin-data-cleanup',
      title: '数据清理',
      path: '/admin/data-cleanup',
      closable: true
    })
  } else if (key === 'admin-burst-state-coef') {
    addTab({
      key: 'admin-burst-state-coef',
      title: '爆链状态系数',
      path: '/admin/burst-state-coef',
      closable: true
    })
  } else if (key === 'admin-ops-f') {
    addTab({
      key: 'admin-ops-f',
      title: '运维 F 值',
      path: '/admin/ops-f',
      closable: true
    })
  } else if (key === 'admin-ops-adjustment-amplitude') {
    addTab({
      key: 'admin-ops-adjustment-amplitude',
      title: '运维调整幅度',
      path: '/admin/ops-adjustment-amplitude',
      closable: true
    })
  } else if (key === 'admin-ops-auto-opt') {
    addTab({
      key: 'admin-ops-auto-opt',
      title: '自动优配',
      path: '/admin/ops-auto-opt',
      closable: true
    })
  } else if (key === 'admin-ops-rp-rankings') {
    addTab({
      key: 'admin-ops-rp-rankings',
      title: 'RP 名单',
      path: '/admin/ops-rp-rankings',
      closable: true
    })
  } else if (key === 'admin-roi-rule-restore') {
    addTab({
      key: 'admin-roi-rule-restore',
      title: 'ROI规则回滚',
      path: '/admin/roi-rule-restore',
      closable: true
    })
  } else if (key === 'report-data') {
    addTab({
      key: 'report-data',
      title: '工具',
      path: '/report-data',
      closable: true
    })
  }
}
</script>

<style scoped>
.app-sidebar {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  z-index: 100;
  background: #fff;
  border-right: 1px solid #e0e0e6;
  transition: width 0.2s ease;
  overflow: hidden;
}

.sidebar-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.sidebar-logo {
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  gap: 10px;
  border-bottom: 1px solid #e0e0e6;
  flex-shrink: 0;
}

.logo-text {
  font-size: 16px;
  font-weight: 600;
}

.sidebar-menu-wrapper {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* 自定义滚动条样式 */
.sidebar-menu-wrapper::-webkit-scrollbar {
  width: 6px;
}

.sidebar-menu-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-menu-wrapper::-webkit-scrollbar-thumb {
  background: #d4d4d8;
  border-radius: 3px;
}

.sidebar-menu-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a1a1aa;
}

.sidebar-user {
  flex-shrink: 0;
  padding: 16px 20px;
  border-top: 1px solid #e0e0e6;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
}

.sidebar-user-collapsed {
  justify-content: center;
  padding: 16px 10px;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
